import * as RadixSelect from "@radix-ui/react-select";
import type { CSSProperties } from "react";
import { Menu } from "lucide-react";
import "./Select.scss";

const SELECT_ICON_CH_OFFSET = 4;

const getSelectMinCh = (
  options: Record<string, string>,
  placeholder?: string,
): number => {
  const labels = Object.values(options);
  if (placeholder) labels.push(placeholder);
  const longest = labels.reduce((max, label) => Math.max(max, label.length), 0);
  return longest + SELECT_ICON_CH_OFFSET;
};

interface SelectProps {
  options: Record<string, string>;
  placeholder?: string;
  /** Padding and font density tier */
  density?: "compact" | "default" | "spacious";
  /** Hover/active transform intensity (independent of density) */
  motionScale?: "sm" | "base" | "lg";
  value?: string;
  variant?: "primary" | "secondary";
  /** Stretch trigger to 100% of parent width */
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  onChange?: (value: string) => void;
}

const Select = ({
  options,
  placeholder,
  value,
  density = "default",
  motionScale = "base",
  variant = "secondary",
  fullWidth = true,
  className,
  disabled = false,
  id,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  onChange,
}: SelectProps) => {
  const triggerClassName = [
    "select__trigger",
    `select__trigger--${variant}`,
    `select__trigger--${density}`,
    `select__trigger--motion-${motionScale}`,
    fullWidth && "select__trigger--full-width",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const triggerStyle = fullWidth
    ? undefined
    : ({
        "--select-min-ch": `${getSelectMinCh(options, placeholder)}ch`,
      } as CSSProperties);

  return (
    <RadixSelect.Root value={value} onValueChange={onChange} disabled={disabled}>
      <RadixSelect.Trigger
        id={id}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        className={triggerClassName}
        style={triggerStyle}
      >
        <div className="select__value">
          <RadixSelect.Value placeholder={placeholder} />
        </div>
        <RadixSelect.Icon className="select__icon-wrapper">
          <Menu className={`select__icon select__icon--${density}`} />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content className="select__content" position="popper">
          <RadixSelect.ScrollUpButton />
          <RadixSelect.Viewport>
            {Object.entries(options).map(([key, label]) => (
              <RadixSelect.Item
                key={key}
                className={`select__item select__item--${density} select__item--${variant}`}
                value={key}
              >
                <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
                <RadixSelect.ItemIndicator />
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
};

export default Select;
